import React, {
  createContext,
  useEffect,
  useState,
  useCallback,
  useContext,
  useRef,
} from "react";
import io from "socket.io-client";
import useOnlineStatus from "../../util/OnlineStatus";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { getDeviceId } from "../../util/deviceIdentity";

const SocketContext = createContext();

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  (process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace(/\/api\/v1\/?$/, "")
    : "https://dev.api.prontopsy.com");

export const SocketProvider = ({ children }) => {
  // React state only for re-rendering listeners; source of truth is the ref
  const [socket, setSocket] = useState(null);
  const socketRef = useRef(null);
  const connectingRef = useRef(false);
  const isOnline = useOnlineStatus();

  const handleForceLogout = useCallback((payload = {}) => {
    const message =
      payload?.message ||
      "You were signed out because your account was used on another device.";
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userDetails");
    localStorage.removeItem("appointmentData");
    localStorage.removeItem("joined");
    localStorage.removeItem("manualOfflineStatus");
    Cookies.remove("auth_token");
    toast.info(message, {
      autoClose: 6000,
    });
    // Bug 14 fix: a call page (VideoCallClose / TextChat) arms a native
    // beforeunload confirmation while a session is active. Without this flag,
    // this forced redirect triggers that "leave page?" dialog, and clicking
    // Cancel/Close aborts the navigation — leaving the loser device frozen on
    // the stale call frame instead of being kicked to /signin.
    try {
      window.__prontoBypassUnloadGuard = true;
    } catch (_) {
      /* ignore */
    }
    window.location.href = "/signin";
  }, []);

  const connectSocket = useCallback(() => {
    const token = localStorage.getItem("auth_token") || Cookies.get("auth_token");
    if (!token) return;
    const deviceId = getDeviceId();

    // Already have a live or reconnecting Socket.IO instance — do not create another
    if (socketRef.current) {
      if (socketRef.current.connected || socketRef.current.active) {
        return;
      }
    }
    if (connectingRef.current) return;

    connectingRef.current = true;

    if (socketRef.current) {
      try {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
      } catch (_) {
        /* ignore */
      }
      socketRef.current = null;
    }

    const newSocket = io(SOCKET_URL, {
      auth: { token, deviceId },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
      // Let Socket.IO manage reconnect only — no custom reconnect loop
    });

    newSocket.on("connect", () => {
      connectingRef.current = false;
      socketRef.current = newSocket;
      setSocket(newSocket);
      // Respect intentional offline (do not force online on every reconnect/refresh)
      try {
        const manualOffline = localStorage.getItem("manualOfflineStatus") === "offline";
        if (manualOffline) {
          newSocket.emit("userOffline");
        } else {
          // Only go online if DB/profile already online — emit online to refresh presence
          // when user is not intentionally offline
          const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
          if (ud?.onlineStatus === "online" || ud?.onlineStatus === "busy") {
            newSocket.emit("userOnline");
          }
          // If offline in profile, stay offline until user taps Online
        }
      } catch (_) {
        /* ignore */
      }
    });

    newSocket.on("disconnect", (reason) => {
      connectingRef.current = false;
      // Do NOT call connectSocket() here — Socket.IO reconnection:true handles it
      // Only clear React state if we intentionally closed the client
      if (reason === "io client disconnect") {
        socketRef.current = null;
        setSocket(null);
      }
    });

    newSocket.on("connect_error", (error) => {
      console.error("Socket connection error:", error?.message || error);
      connectingRef.current = false;
    });

    newSocket.on("forceLogout", (payload) => {
      handleForceLogout(payload);
    });

    socketRef.current = newSocket;
    setSocket(newSocket);
  }, [handleForceLogout]);

  const disconnectSocket = useCallback(() => {
    connectingRef.current = false;
    if (socketRef.current) {
      try {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
      } catch (_) {
        /* ignore */
      }
      socketRef.current = null;
      setSocket(null);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("auth_token") || Cookies.get("auth_token");
    if (token && isOnline) {
      connectSocket();
    } else if (!token) {
      disconnectSocket();
    }
    // When isOnline goes false, keep the socket instance; Socket.IO will reconnect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  // Re-connect when auth token appears after login
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "auth_token" && e.newValue) {
        connectSocket();
      }
      if (e.key === "auth_token" && !e.newValue) {
        disconnectSocket();
      }
    };
    window.addEventListener("storage", onStorage);
    // Initial
    const token = localStorage.getItem("auth_token") || Cookies.get("auth_token");
    if (token) connectSocket();

    return () => {
      window.removeEventListener("storage", onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      disconnectSocket();
    };
  }, [disconnectSocket]);

  // Do NOT emit userOffline on beforeunload — browser is not authoritative.
  // Server marks offline when last socket for the user disconnects.

  return (
    <SocketContext.Provider
      value={{
        socket,
        socketRef,
        connectSocket,
        reconnectSocket: connectSocket,
        disconnectSocket,
      }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = (eventName, callback, dependencies = []) => {
  const { socket } = useContext(SocketContext);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedCallback = useCallback(callback || (() => {}), dependencies);

  useEffect(() => {
    if (!socket || !eventName) return;

    socket.on(eventName, memoizedCallback);

    return () => {
      socket.off(eventName, memoizedCallback);
    };
  }, [socket, eventName, memoizedCallback]);

  return socket;
};

export default SocketContext;
